// Cross-backend client for the unified-portal Account Settings tabs that must talk
// to admin-corev2 from INSIDE pitstop-ui (My Account, Admin Notifications, eBL
// Templates). pitstop-ui's global axios pins every request to pitstop-core
// (Channel: UI + X-Request-From: pitstop + X-Pitstop-Hostname + the pitstop
// Bearer token) and its shared handleError CLEARS the session on 401 — all wrong
// for admin-directed calls. So we use native fetch with explicit admin headers and
// LOCAL 401 handling (a stale admin token must NOT wipe the pitstop session).
//
// Reverse cross-backend contract (verified 2026-07-09): admin-corev2 validates the
// token from Authorization regardless of Channel, so NO `Channel: UI` and NO
// `X-Pitstop-Hostname` — just Authorization + dex + X-Request-From: admin. Native
// paths /api/v1/*. This client is only used in shell-hosted mode.

const ADMIN_BASE = '/api/v1';

function adminHeaders() {
	const token = sessionStorage.getItem('adminCoreToken');
	const dex = sessionStorage.getItem('selectedDex');
	return {
		'Content-Type': 'application/json',
		...(token ? { Authorization: `Bearer ${token}` } : {}),
		'X-Request-From': 'admin',
		...(dex ? { dex } : {}),
	};
}

// Relative admin-corev2 call → prefixes /api/v1, sends admin headers, returns [error, data].
export async function adminRequest(method, path, body) {
	try {
		const res = await fetch(`${ADMIN_BASE}${path}`, {
			method,
			headers: adminHeaders(),
			...(body !== undefined ? { body: JSON.stringify(body) } : {}),
		});

		if (res.status === 401) {
			return [{ status: 401, sessionExpired: true }, undefined];
		}
		if (!res.ok) {
			let errBody;
			try { errBody = await res.json(); } catch { errBody = null; }
			return [errBody?.error || errBody || `Request failed (${res.status})`, undefined];
		}
		let json;
		try { json = await res.json(); } catch { json = undefined; }
		// Mirror services/api/utils/response.js: unwrap a nested { data } envelope.
		const data = json && Object.hasOwn(json, 'data') ? json.data : json;
		return [undefined, data];
	} catch (err) {
		return [err, undefined];
	}
}

// Proactively refresh the admin token when a cross-backend tab opens. In
// pitstop-ui the admin token ages independently (its renew timer only runs while
// the admin SPA is mounted, which it isn't here), so without this a long
// pitstop-only session leaves a near-stale admin token → 401 on first use.
// Best-effort: on success writes the fresh token back; a 401 here means it's
// already expired server-side (caller shows the session-expired state).
export async function renewAdminToken() {
	const [error, data] = await adminRequest('POST', '/renew-token');
	const token = data?.token;
	if (!error && token) sessionStorage.setItem('adminCoreToken', token);
	return [error, data];
}

// Cross-origin PUT to the presigned S3 URL. NO admin headers (the bucket CORS
// allowlist rejects Authorization/X-Request-From; auth rides the URL's query
// string). Raw file body + the file's own Content-Type.
export async function s3Put(uploadUrl, file) {
	try {
		const res = await fetch(uploadUrl, {
			method: 'PUT',
			headers: { 'Content-Type': file.type },
			body: file,
		});
		if (!res.ok) return [`S3 upload failed (${res.status})`, undefined];
		return [undefined, {}];
	} catch (err) {
		return [err, undefined];
	}
}
